import React from "react";
import {
  Grid,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { initialFilterData } from "../reports/utils/ReportTools";
import SummaryIcon from "src/icons/transactions/SummaryIcon";
import DoubleCell from "./DoubleCell";
import FiltersIcon from "src/icons/transactions/FiltersIcon";

import type { Theme } from "@mui/material/styles";
import { makeStyles, createStyles } from "@mui/styles";
import TransactionReports from "./TransactionReports";
import useHoverStyles from "./transactionHooks/useHoverStyles";
import { FilterData } from "./types";
import MaintananceTooltip from "../maintenance/MaintenanceToolTip";
import { useMaintenanceState } from "src/store/features/maintenance.slice";
import EofyStatementButton from "./EofyStatementButton";

export const FilterStyles = makeStyles((theme: Theme) =>
  createStyles({
    filterStack: {
      backgroundColor: theme.palette.primary.main,
      height: "49px",
      padding: "12px",
    },
    toggleButtonGroup: {
      "& > *": {
        textTransform: "none",
        paddingRight: "18px",
        border: "none",
        height: "12px",
        whiteSpace: "nowrap",
      },
      "&:hover": {
        color: "red",
      },
    },
    toggleButton: {
      color: "#fff",
      "&:hover": {
        color: theme.palette.secondary.main,
      },
    },
  }),
);

interface FilterProps {
  type: string;
  displayFilter: boolean;
  setDisplayFilter: React.Dispatch<React.SetStateAction<boolean>>;
  onChange: (event: React.MouseEvent<HTMLElement>, newAlignment: any) => void;
}

const Filter = (props: FilterProps) => {
  const { displayFilter, type, onChange, setDisplayFilter } = props;
  const classes = FilterStyles();
  const { reportingcontroller } = useMaintenanceState();
  const [state, toggleExportModal] = React.useReducer((state) => !state, false);
  const [reportsFilter, setReportsFilter] = React.useState<FilterData>(initialFilterData);

  const theme = useTheme();
  const showText = useMediaQuery(theme.breakpoints.between(900, 1070));

  const handleFilterOnClick = (e: any) => {
    setDisplayFilter((prev) => !prev);
    setSelectedFilter((prev) => !prev);
  };

  const {
    onEnter: handleSummaryHoverEnter,
    onLeave: handleSummaryHoverLeave,
    styles: summaryHoverStyles,
  } = useHoverStyles();

  const {
    onEnter: handleFilterHoverEnter,
    onLeave: handleFilterHoverLeave,
    styles: filterHoverStyles,
  } = useHoverStyles();

  const [selectedFilter, setSelectedFilter] = React.useState<boolean>(false);

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      className={classes.filterStack}
      sx={{ boxShadow: displayFilter ? "0 6px 12px 0 #c4ced4" : "" }}>
      <ToggleButtonGroup
        color="secondary"
        value={type}
        exclusive
        onChange={onChange}
        className={classes.toggleButtonGroup}>
        <ToggleButton id="transactions-filter-all" value="all" className={classes.toggleButton}>
          All
        </ToggleButton>
        <ToggleButton
          id="transactions-filter-deposit"
          value="deposit"
          className={classes.toggleButton}>
          Deposits
        </ToggleButton>
        <ToggleButton
          id="transactions-filter-withdrawal"
          value="withdrawal"
          className={classes.toggleButton}>
          Withdrawals
        </ToggleButton>
        <ToggleButton id="transactions-filter-trade" value="trade" className={classes.toggleButton}>
          Trades
        </ToggleButton>
        <ToggleButton
          id="transactions-filter-target"
          value="target"
          className={classes.toggleButton}>
          Targets
        </ToggleButton>
        <ToggleButton
          id="transactions-filter-stoplimit"
          value="stop_limit"
          className={classes.toggleButton}>
          Stop Limits
        </ToggleButton>
      </ToggleButtonGroup>

      <Grid container justifyContent="flex-end" alignItems="center">
        {<EofyStatementButton />}
        <Grid item>
          <MaintananceTooltip
            message="Reporting are undergoing maintenance. If you need assistance, please contact support"
            disableHover={reportingcontroller}>
            <IconButton
              aria-label="summary"
              size="small"
              disabled={!reportingcontroller}
              onClick={toggleExportModal}
              onMouseEnter={handleSummaryHoverEnter}
              onMouseLeave={handleSummaryHoverLeave}
              sx={{
                color: summaryHoverStyles.color,
                fontSize: "14px",
                "&.Mui-disabled": {
                  color: summaryHoverStyles.color,
                  opacity: 0.5,
                },
              }}>
              <DoubleCell
                left={showText ? "" : "Summary"}
                right={<SummaryIcon color={summaryHoverStyles.color} />}
                tooltip="Summary"
              />
            </IconButton>
          </MaintananceTooltip>
        </Grid>
        <Grid item>
          <IconButton
            id="transactions-filter-button"
            aria-label="filter"
            size="small"
            onClick={handleFilterOnClick}
            onMouseEnter={handleFilterHoverEnter}
            onMouseLeave={handleFilterHoverLeave}
            sx={{
              color: selectedFilter ? "#8d9da7" : filterHoverStyles.color,
              fontSize: "14px",
            }}>
            <DoubleCell
              left={showText ? "" : "Filters"}
              right={<FiltersIcon color={selectedFilter ? "#8d9da7" : filterHoverStyles.color} />}
              tooltip="Filters"
            />
          </IconButton>
        </Grid>
      </Grid>

      <TransactionReports
        state={state}
        toggleExportModal={toggleExportModal}
        reportsFilter={reportsFilter}
        setReportsFilter={setReportsFilter}
      />
    </Stack>
  );
};

export default Filter;
